'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { FaDiscord } from 'react-icons/fa';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { cn, EASE_PREMIUM as EASE } from '@sharpr/ui';
import { GLASS_CARD } from './shared';

const VISIBLE = 3;
const TICK_MS = 2800;

// useLayoutEffect on the client (measure before paint), useEffect on SSR (no warn).
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;
// Delay before the lineup message flips Projected → Confirmed, from in-view.
const SWAP_MS = 1400;

/** Lineup status pill — amber while projected, emerald once locked. */
function StatusPill({ status }: { status: 'Projected' | 'Confirmed' }) {
  return (
    <motion.span
      key={status}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.3, ease: EASE }}
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold leading-none',
        status === 'Projected'
          ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
          : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
      )}
    >
      {status}
    </motion.span>
  );
}

/**
 * A projected lineup locking in with a bat swapped. The slot line reserves its
 * space pre-swap (opacity only) so the stack doesn't jump when it reveals.
 */
function LineupSwapBody() {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { amount: 0.5 });
  const [status, setStatus] = useState<'Projected' | 'Confirmed'>(
    reduced ? 'Confirmed' : 'Projected',
  );
  useEffect(() => {
    if (!inView || status === 'Confirmed') return;
    // Reduced motion settles immediately — handled here too because framer's
    // useReducedMotion can resolve null → true after the first render.
    if (reduced) {
      setStatus('Confirmed');
      return;
    }
    const id = setTimeout(() => setStatus('Confirmed'), SWAP_MS);
    return () => clearTimeout(id);
  }, [reduced, inView, status]);
  const confirmed = status === 'Confirmed';
  return (
    <span ref={ref} className="block">
      <span className="flex items-center gap-1.5">
        DET lineup vs MIN
        <AnimatePresence mode="wait" initial={false}>
          <StatusPill status={status} />
        </AnimatePresence>
      </span>
      <motion.span
        animate={{ opacity: confirmed ? 1 : 0 }}
        transition={{ duration: 0.35, ease: EASE }}
        className="mt-1 block font-mono text-xs text-fg/70"
        aria-hidden={!confirmed}
      >
        Slot 5: ⭐ Greene → Pérez
      </motion.span>
    </span>
  );
}

/** Directional stat line: market + arrow + move size, +EV side when one opens up. */
function StatLine({
  market,
  up,
  pct,
  evSide,
}: {
  market: string;
  up: boolean;
  pct: number;
  evSide?: 'Over' | 'Under';
}) {
  return (
    <span className="flex items-center gap-1.5 font-mono text-xs text-fg/70">
      <span className="w-9 uppercase">{market}</span>
      <span className={up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}>
        {up ? '↑' : '↓'} {pct}%
      </span>
      {evSide && (
        <span className="font-sans font-semibold text-emerald-600 dark:text-emerald-400">
          → {evSide} +EV
        </span>
      )}
    </span>
  );
}

// Feed story order: lineup confirms with a swap → Ober reprojected → NBA star
// sits → conditionals reprice his teammates.
const MESSAGES: { channel: string; time: string; body: React.ReactNode }[] = [
  {
    channel: '#mlb-lineups',
    time: '2:41 PM',
    body: <LineupSwapBody />,
  },
  {
    channel: '#mlb-projections',
    time: '2:41 PM',
    body: (
      <span className="block">
        <span className="font-semibold text-fg">Bailey Ober</span> reprojected
        off the swap:
        <span className="mt-1 block space-y-0.5">
          <StatLine market="K" up pct={6} evSide="Over" />
          <StatLine market="ER" up={false} pct={5} />
          <StatLine market="Outs" up pct={3} />
        </span>
      </span>
    ),
  },
  {
    channel: '#nba-news',
    time: '6:12 PM',
    body: (
      <>
        Giannis (knee) is{' '}
        <span className="font-semibold text-red-500 dark:text-red-400">OUT</span>{' '}
        tonight.
      </>
    ),
  },
  {
    channel: '#nba-projections',
    time: '6:13 PM',
    body: (
      <>
        Conditionals are live for MIL. Every teammate repriced with Giannis
        off the floor.
      </>
    ),
  },
];

function FeedMessage({ channel, time, body }: (typeof MESSAGES)[number]) {
  return (
    <div className="flex items-start gap-3 px-4 sm:px-5 py-2.5">
      <Image
        src="/sharpr-icon.png"
        alt=""
        width={36}
        height={36}
        className="h-9 w-9 shrink-0 rounded-full bg-fg/5 mt-0.5"
      />
      <div className="min-w-0 text-left">
        {/* Channel + timestamp stay unbreakable; "Today at" prefix drops at phone width. */}
        <p className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 leading-none">
          <span className="text-sm font-semibold text-fg">Sharpr</span>
          <span className="rounded bg-brand-500 px-1 py-0.5 text-[9px] font-bold leading-none text-white">
            BOT
          </span>
          <span className="whitespace-nowrap text-[11px] text-fg/60">{channel}</span>
          <span className="whitespace-nowrap text-[10px] text-fg/50">
            <span className="hidden sm:inline">Today at </span>
            {time}
          </span>
        </p>
        <p className="mt-1 text-sm leading-snug text-fg/80">{body}</p>
      </div>
    </div>
  );
}

/**
 * Discord-flavored alert feed for the news section. The stack starts full and
 * cycles while on screen; the lineup alert plays its Projected → Confirmed swap
 * on scroll-in. Reduced motion renders a static stack.
 */
export function DiscordNewsFeed({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.4 });
  const reduced = useReducedMotion();

  // `count` = total messages delivered; window = last VISIBLE, circular over MESSAGES.
  const [count, setCount] = useState(VISIBLE);
  useEffect(() => {
    if (!inView || reduced) return;
    const id = setInterval(() => setCount((c) => c + 1), TICK_MS);
    return () => clearInterval(id);
  }, [inView, reduced]);

  // Fixed stack height (so cycling never resizes the card) = tallest VISIBLE-message
  // window, measured from a hidden render at real width. Fallback never paints (layout
  // effect runs before paint).
  const measureRef = useRef<HTMLDivElement>(null);
  const [stackHeight, setStackHeight] = useState<number | null>(null);
  useIsomorphicLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const compute = () => {
      const heights = Array.from(el.children, (c) => (c as HTMLElement).offsetHeight);
      if (heights.length === 0) return;
      let max = 0;
      for (let i = 0; i < heights.length; i++) {
        let window = 0;
        for (let j = 0; j < VISIBLE; j++) window += heights[(i + j) % heights.length];
        max = Math.max(max, window);
      }
      setStackHeight(max + 16); // + the stack's py-2
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const visible = Array.from({ length: VISIBLE }, (_, i) => {
    const seq = count - VISIBLE + i;
    return { seq, msg: MESSAGES[seq % MESSAGES.length] };
  });

  return (
    <div
      ref={ref}
      className={cn(GLASS_CARD, 'relative w-full max-w-md mx-auto', className)}
    >
      {/* Hidden measurement render — feeds the stack-height calculation above. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="invisible pointer-events-none absolute inset-x-0 top-0 -z-10"
      >
        {MESSAGES.map((msg, i) => (
          <FeedMessage key={i} {...msg} />
        ))}
      </div>

      {/* Board header */}
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-2.5 border-b border-[var(--glass-border)]">
        <p className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.14em] uppercase text-fg/60 whitespace-nowrap">
          <FaDiscord className="h-[15px] w-[15px] text-[#5865F2]" />
          Sharpr Discord
        </p>
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"
        />
      </div>

      {/* Message stack — fixed measured height. Messages animate their own height
          in flow (no popLayout) so mixed-height notifications push smoothly. */}
      <div
        className="flex flex-col overflow-hidden py-2"
        style={{ height: stackHeight ?? 304 }}
      >
        <AnimatePresence initial={false}>
          {visible.map(({ seq, msg }) => (
            <motion.div
              key={seq}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="overflow-hidden"
            >
              <FeedMessage {...msg} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Footer caption */}
      <div className="border-t border-[var(--glass-border)] px-4 py-2 text-center text-[11px] text-fg/60">
        Included with every subscription
      </div>
    </div>
  );
}
